import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from '@/utils/icons';
import { cn } from '@/lib/utils';

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'dls-flex dls-h-9 dls-w-full dls-items-center dls-justify-between dls-whitespace-nowrap dls-rounded-md dls-border dls-border-input dls-bg-transparent dls-px-3 dls-py-2 dls-text-sm dls-shadow-10 dls-ring-offset-background placeholder:dls-text-muted-foreground focus:dls-outline-none focus:dls-ring-1 focus:dls-ring-ring disabled:dls-cursor-not-allowed disabled:dls-opacity-50 [&>span]:dls-line-clamp-1',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="dls-h-4 dls-w-4 dls-opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('dls-flex dls-cursor-default dls-items-center dls-justify-center dls-py-1', className)}
    {...props}
  >
    <ChevronUp className="dls-h-4 dls-w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('dls-flex dls-cursor-default dls-items-center dls-justify-center dls-py-1', className)}
    {...props}
  >
    <ChevronDown className="dls-h-4 dls-w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'dls-relative dls-z-50 dls-max-h-96 dls-min-w-[8rem] dls-overflow-hidden dls-rounded-md dls-border dls-bg-popover dls-text-popover-foreground dls-shadow-20 data-[state=open]:dls-animate-in data-[state=closed]:dls-animate-out data-[state=closed]:dls-fade-out-0 data-[state=open]:dls-fade-in-0 data-[state=closed]:dls-zoom-out-95 data-[state=open]:dls-zoom-in-95 data-[side=bottom]:dls-slide-in-from-top-2 data-[side=left]:dls-slide-in-from-right-2 data-[side=right]:dls-slide-in-from-left-2 data-[side=top]:dls-slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:dls-translate-y-1 data-[side=left]:-dls-translate-x-1 data-[side=right]:dls-translate-x-1 data-[side=top]:-dls-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'dls-p-1',
          position === 'popper' &&
            'dls-h-[var(--radix-select-trigger-height)] dls-w-full dls-min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('dls-px-2 dls-py-1.5 dls-text-sm dls-font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'dls-relative dls-flex dls-w-full dls-cursor-default dls-select-none dls-items-center dls-rounded-sm dls-py-1.5 dls-pl-2 dls-pr-8 dls-text-sm dls-outline-none focus:dls-bg-accent focus:dls-text-accent-foreground data-[disabled]:dls-pointer-events-none data-[disabled]:dls-opacity-50',
      className
    )}
    {...props}
  >
    <span className="dls-absolute dls-right-2 dls-flex dls-h-3.5 dls-w-3.5 dls-items-center dls-justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="dls-h-4 dls-w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-dls-mx-1 dls-my-1 dls-h-px dls-bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
